import { colorForValue } from "./colorScales";
import { ColorRamp } from "./types";

/**
 * Pure computation of the cluster badges: what to show, where, in which
 * color and size. The GeoMapCanvas effect only diffs the result against its
 * live DOM markers, so everything testable lives here, map-free.
 */

export interface ClusterBadge {
  clusterId: number;
  countLabel: string;
  lngLat: [number, number];
  color: string;
  size: number;
}

/** Minimal structural view of a rendered source feature (maplibre-free). */
export interface ClusterSourceFeature {
  properties: { [name: string]: unknown };
  geometry: { type: string; coordinates?: unknown };
}

/** Badge diameter for a cluster of `count` points. */
export function clusterSize(count: number): number {
  if (count >= 50) return 48;
  if (count >= 10) return 40;
  return 32;
}

/**
 * Map rendered source features to badges, keyed by cluster id.
 *
 * Skips non-cluster features, and deduplicates clusters that appear in
 * several tiles (querySourceFeatures returns one copy per tile). The badge
 * color is the ramp applied to the cluster's average value.
 */
export function computeClusterBadges(
  features: readonly ClusterSourceFeature[],
  ramp: ColorRamp
): Map<number, ClusterBadge> {
  const badges = new Map<number, ClusterBadge>();

  for (const feature of features) {
    const { cluster_id, point_count, point_count_abbreviated, valueSum } =
      feature.properties;
    if (
      typeof cluster_id !== "number" ||
      typeof point_count !== "number" ||
      point_count <= 0 ||
      typeof valueSum !== "number" ||
      badges.has(cluster_id)
    ) {
      continue;
    }

    if (feature.geometry.type !== "Point") continue;
    const coordinates = feature.geometry.coordinates;
    if (!Array.isArray(coordinates)) continue;
    const [lng, lat] = coordinates;
    if (typeof lng !== "number" || typeof lat !== "number") continue;

    badges.set(cluster_id, {
      clusterId: cluster_id,
      countLabel: String(point_count_abbreviated ?? point_count),
      lngLat: [lng, lat],
      color: colorForValue(ramp, valueSum / point_count),
      size: clusterSize(point_count),
    });
  }

  return badges;
}
